import React, { useEffect, useState } from 'react'
import { useTransition } from 'react-spring'
import { CarouselCard, CarouselIconButton, DotsWrapper } from './CarouselComponent.css'
import { useToggleState } from 'lib/hooks/useToggleState'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { Body1, ButtonText, Headline4 } from 'stories/elements/Typography/Text'
import NextImage from 'components/elements/NextImage'
import BlueArrow from 'assets/pro-plan/blue_arrow.svg'
import _useTranslation from 'lib/hooks/_useTranslation'
import NewButton from 'components/elements/NewButton'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventProPlanBenefitClick } from 'lib/constants/AnalyticsEventType'

interface CarouselDotProps {
    isClicked: boolean
}

const Component = styled.div`
    display: flex;
    flex-direction: column;
    gap: 50px;
    align-items: center;
    @media (max-width: 750px) {
        gap: 30px;
    }
`

const Container = styled.div`
    display: flex;
    width: 100%;
    align-self: center;
    justify-self: center;
    height: fit-content;
    .circle {
        width: 60px !important;
        height: 60px !important;
        svg {
            position: relative;
            left: 0;
        }
    }
    @media (max-width: 750px) {
        button {
            display: none;
        }
    }
`

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: flex-end;
    transform: translateZ(0);
    height: 575px;
`

const Card = styled.div`
    position: relative;
    width: 100%;
    overflow: hidden;
    height: 650px;
    background: linear-gradient(0deg, rgba(10, 10, 10, 0.05), rgba(10, 10, 10, 0.05)),
        linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15));
    padding: 25px 5%;
    border-radius: ${theme.borderRadius.normal};
    display: flex;
    justify-content: center;
    .asset {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    button {
        position: absolute;
        bottom: 20px;
    }
    .text {
        padding: 10px;
        max-width: 450px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 5px;
    }
    @media (max-width: 750px) {
        width: 100%;
        height: 550px;
        flex-direction: column;
        gap: 0;
        img {
            height: 250px;
            width: 100%;
            border-radius: ${theme.borderRadius.large} ${theme.borderRadius.large} 0 0;
            order: 0;
        }
        .text {
            height: auto;
            order: 1;
            padding: 15px;
            h4,
            h5,
            p {
                text-align: center;
            }
        }
    }
`

const CarouselDot = styled.div<CarouselDotProps>`
    height: 5px;
    width: 60px;
    background-color: ${({ isClicked }) => (isClicked ? theme.colors.white : `${theme.colors.white}33`)};
    border-radius: ${theme.borderRadius.small};
    z-index: 9;
    margin: 0 5px;
    cursor: pointer;
    z-index: 1;
    @media (max-width: 750px) {
        margin: 22.5px 10px;
        height: 6px;
        width: 50px;
    }
`

const Arrow = styled(BlueArrow)<{ isReverted?: boolean }>`
    transform: ${({ isReverted }) => `rotate(${isReverted ? 0 : 180}deg)`};
`

interface CarouselProps {
    carouselData: {
        text: string
        image: StaticImageData
        title: string
        eventValue: string
    }[]
    onButtonClick: (value: string) => void
    referrerScreen?: string
}

export default function BenefitCarousel({ carouselData, onButtonClick, referrerScreen }: CarouselProps) {
    const { t } = _useTranslation('plusPlan')
    const [count, setCount] = useState(0)
    const [direction, toggleDirection] = useToggleState(false)
    const md = useMedia(deviceSize.md)

    const transitions = useTransition([count], (item) => item, {
        from: {
            transform: `translate3d(${direction ? '' : '-'}${md ? 0 : '25%'},0,0)`,
            opacity: 0,
            position: 'absolute',
        },
        enter: { transform: 'translate3d(0,0,0)', opacity: 1 },
        leave: {
            opacity: 0,
            transform: `translate3d(${direction ? '-' : ''}${md ? 0 : '25%'},0,0)`,
        },
    })

    useEffect(() => {
        trackEvent(AnalyticsEventProPlanBenefitClick, { referrerScreen, value: carouselData[count].eventValue })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count])

    const prevSlide = () => {
        if (direction) toggleDirection()
        const prevSlide = count - 1 < 0 ? carouselData.length - 1 : count - 1
        setCount(prevSlide)
    }

    const nextSlide = () => {
        if (!direction) toggleDirection()
        const nextSlide = count + 1 < carouselData.length ? count + 1 : 0
        setCount(nextSlide)
    }

    const toggleDot = (index: number) => {
        if (index > count) {
            if (!direction) toggleDirection()
        } else {
            if (direction) toggleDirection()
        }
        setCount(index)
    }

    return (
        <Component>
            {!md ? (
                <DotsWrapper>
                    {carouselData.map((_, i) => (
                        <CarouselDot key={`carousel-dot-${i}`} isClicked={count === i} onClick={() => toggleDot(i)} />
                    ))}
                </DotsWrapper>
            ) : null}
            <Container>
                {!md ? (
                    <CarouselIconButton onClick={prevSlide} isLeft revertColors moveButtonsInsideCard>
                        <div className="circle">
                            <Arrow />
                        </div>
                    </CarouselIconButton>
                ) : null}
                <Wrapper>
                    {transitions.map(({ item, props, key }) => {
                        const data = carouselData[item]
                        return (
                            <CarouselCard key={key} style={props} fullWidth>
                                <Card>
                                    <div className="asset">
                                        <NextImage
                                            src={data.image.src}
                                            alt="Testimonial user"
                                            width={300}
                                            height={300}
                                            objectFit="scale-down"
                                        />
                                    </div>
                                    <div className="text">
                                        <Headline4 textAlign="center" color={theme.colors.white}>
                                            {data.title}
                                        </Headline4>

                                        <Body1
                                            textAlign="center"
                                            fontSize={18}
                                            fontWeight={400}
                                            fontFamily="var(--font-inter)"
                                            color={theme.colors.lighterWhite}
                                        >
                                            {data.text}
                                        </Body1>
                                    </div>
                                    <NewButton
                                        borderRadius={theme.borderRadius.normal}
                                        color={theme.colors.white}
                                        width={md ? undefined : 450}
                                        fullWidth={md}
                                        onClick={() => onButtonClick(data.eventValue)}
                                    >
                                        <ButtonText color={theme.colors.businessBlue}>
                                            {t('plusPlan/getAllBenefitsButtonLabel')}
                                        </ButtonText>
                                    </NewButton>
                                </Card>
                            </CarouselCard>
                        )
                    })}
                </Wrapper>
                {!md ? (
                    <CarouselIconButton onClick={nextSlide} revertColors moveButtonsInsideCard>
                        <div className="circle">
                            <Arrow isReverted />
                        </div>
                    </CarouselIconButton>
                ) : null}
            </Container>
            {md ? (
                <DotsWrapper>
                    <CarouselIconButton onClick={prevSlide} revertColors>
                        <div className="circle">
                            <Arrow />
                        </div>
                    </CarouselIconButton>

                    {carouselData.map((_, i) => (
                        <CarouselDot key={`carousel-dot-${i}`} isClicked={count === i} onClick={() => toggleDot(i)} />
                    ))}

                    <CarouselIconButton onClick={nextSlide} revertColors>
                        <div className="circle">
                            <Arrow isReverted />
                        </div>
                    </CarouselIconButton>
                </DotsWrapper>
            ) : null}
        </Component>
    )
}
